import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  alpha
} from '@mui/material';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  Send,
  ChevronRight
} from 'lucide-react';

// Styled link component for footer links with consistent fonts
const FooterLink = styled(RouterLink)(({ theme }) => ({
  color: 'rgba(255,255,255,0.7)',
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  fontSize: '0.9rem',
  '&:hover': {
    color: '#FF5722',
    transform: 'translateX(4px)'
  }
}));

// Updated Logo Component with consistent styling
const Logo = () => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          position: 'relative',
          mr: 1.5,
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 10px rgba(255, 87, 34, 0.3)',
        }}
      >
        {/* Floor Plan Design */}
        <Box sx={{
          position: 'relative',
          width: '75%',
          height: '75%',
          zIndex: 2,
        }}>
          {/* Outer Wall */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: '2px solid white',
          }} />
          
          {/* Inner Walls */}
          <Box sx={{
            position: 'absolute',
            top: '30%',
            left: 0,
            width: '65%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '30%',
            right: 0,
            width: '20%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '65%',
            width: '35%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '65%',
            left: 0,
            width: '60%',
            height: '2px',
            backgroundColor: 'white',
          }} />
          
          <Box sx={{
            position: 'absolute',
            top: '30%',
            left: '65%',
            width: '2px',
            height: '70%',
            backgroundColor: 'white',
          }} />
        </Box>
      </Box>
      <Box>
        <Typography
          variant="h6"
          noWrap
          component={RouterLink}
          to="/"
          sx={{
            fontWeight: 700,
            letterSpacing: '.05rem',
            color: 'white',
            textDecoration: 'none',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            fontSize: { xs: '1.2rem', md: '1.4rem' }
          }}
        >
          Vecto<Box component="span" sx={{ color: '#FF5722' }}>Space</Box>
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.1em',
            fontSize: '0.65rem',
            lineHeight: 1,
            textTransform: 'uppercase',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
          }}
        >
          Interior Design Studio
        </Typography>
      </Box>
    </Box>
  );
};

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentYear = new Date().getFullYear();
  
  // Footer Link Items
  const quickLinks = [
    { text: 'Home', path: '/' },
    { text: 'About Us', path: '/about' },
    { text: 'Services', path: '/services' },
    { text: 'Portfolio', path: '/portfolio' },
    { text: 'Blog', path: '/blog' },
    { text: 'Contact', path: '/contact' }
  ];
  
  const serviceLinks = [
    { text: 'Interior Design', path: '/services/interior-design' },
    { text: '3D Visualization', path: '/services/3d-visualization' },
    { text: 'Commercial Design', path: '/services/commercial-design' },
    { text: 'Residential Design', path: '/services/residential-design' },
    { text: 'Space Planning', path: '/services/space-planning' },
    { text: 'Renovation Services', path: '/services/renovation' }
  ];
  
  return (
    <Box component="footer" sx={{ position: 'relative', overflow: 'hidden' }}>
      {/* Main Footer Section */}
      <Box 
        sx={{ 
          position: 'relative',
          bgcolor: '#121212',
          backgroundImage: 'linear-gradient(to right, #121212, #1E1E1E)',
          pt: 10, 
          pb: 8,
          overflow: 'hidden'
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.5,
            zIndex: 0
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: '-10%',
            width: 350,
            height: 350,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            background: alpha('#FF5722', 0.05),
            filter: 'blur(80px)',
            zIndex: 0
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 3 }}>
                <Logo />
              </Box>
              
              <Typography 
                variant="body2" 
                color="rgba(255,255,255,0.7)" 
                sx={{ 
                  mb: 4, 
                  maxWidth: 350,
                  lineHeight: 1.6,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Turning vision into reality, VectoSpaceDesign creates inspiring spaces 
                with precision craftsmanship and innovative design solutions, elevating 
                the human experience through extraordinary environments.
              </Typography>
              
              {/* Social Media Icons */}
              <Box sx={{ display: 'flex', mb: 4, gap: 1 }}>
                <IconButton 
                  aria-label="Instagram" 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: '#FF5722'
                    },
                    transition: 'all 0.3s ease',
                    width: 38,
                    height: 38
                  }}
                >
                  <Instagram size={18} />
                </IconButton>
                <IconButton 
                  aria-label="Facebook" 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: '#FF5722'
                    },
                    transition: 'all 0.3s ease',
                    width: 38,
                    height: 38
                  }}
                >
                  <Facebook size={18} />
                </IconButton>
                <IconButton 
                  aria-label="Twitter" 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: '#FF5722'
                    },
                    transition: 'all 0.3s ease',
                    width: 38,
                    height: 38
                  }}
                >
                  <Twitter size={18} />
                </IconButton>
                <IconButton 
                  aria-label="LinkedIn" 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: '#FF5722'
                    },
                    transition: 'all 0.3s ease',
                    width: 38,
                    height: 38
                  }}
                >
                  <Linkedin size={18} />
                </IconButton>
                <IconButton 
                  aria-label="YouTube" 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: '#FF5722'
                    },
                    transition: 'all 0.3s ease',
                    width: 38,
                    height: 38
                  }}
                >
                  <Youtube size={18} />
                </IconButton>
              </Box>
              
              {/* Newsletter Subscription */}
              <Typography 
                variant="subtitle1" 
                color="white" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Subscribe to Our Newsletter
              </Typography>
              
              <Box 
                component="form" 
                sx={{ 
                  display: 'flex',
                  mb: 3
                }}
              >
                <TextField
                  placeholder="Enter your email"
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          edge="end" 
                          sx={{ color: '#FF5722' }}
                        >
                          <Send size={18} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: 0,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.1)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.2)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF5722'
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
            
            {/* Quick Links */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography 
                variant="subtitle1" 
                color="white" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                  position: 'relative',
                  display: 'inline-block',
                  pb: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: 40,
                    height: 2,
                    backgroundColor: '#FF5722'
                  }
                }}
              >
                Quick Links
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {quickLinks.map((link, index) => (
                  <FooterLink key={index} to={link.path}>
                    <ChevronRight size={16} style={{ marginRight: 4 }} />
                    {link.text}
                  </FooterLink>
                ))}
              </Box>
            </Grid>
            
            {/* Services */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography 
                variant="subtitle1" 
                color="white" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                  position: 'relative',
                  display: 'inline-block',
                  pb: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: 40,
                    height: 2,
                    backgroundColor: '#FF5722'
                  }
                }}
              >
                Our Services
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {serviceLinks.map((link, index) => (
                  <FooterLink key={index} to={link.path}>
                    <ChevronRight size={16} style={{ marginRight: 4 }} />
                    {link.text}
                  </FooterLink>
                ))}
              </Box>
            </Grid>
            
            {/* Contact Details */}
            <Grid item xs={12} sm={4} md={4}>
              <Typography 
                variant="subtitle1" 
                color="white" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                  position: 'relative',
                  display: 'inline-block',
                  pb: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: 40,
                    height: 2,
                    backgroundColor: '#FF5722'
                  }
                }}
              >
                Contact Us
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', mb: 2.5 }}>
                  <MapPin size={20} style={{ color: '#FF5722', marginRight: 12, marginTop: 4, flexShrink: 0 }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="white" 
                      fontWeight={600} 
                      gutterBottom
                      sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
                    >
                      Hyderabad Office
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="rgba(255,255,255,0.7)"
                      sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
                    >
                      Galaxy Business Park, Level 4,<br />
                      Hi-Tech City Road, Madhapur,<br />
                      Hyderabad, Telangana 500081
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 2.5 }}>
                  <Phone size={20} style={{ color: '#FF5722', marginRight: 12, flexShrink: 0 }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="rgba(255,255,255,0.7)"
                      sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
                    >
                      +91 40 6678 9000
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 2.5 }}>
                  <Mail size={20} style={{ color: '#FF5722', marginRight: 12, flexShrink: 0 }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="rgba(255,255,255,0.7)"
                      sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
                    >
                      info@vectospacedesign.com
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 2.5 }}>
                  <Clock size={20} style={{ color: '#FF5722', marginRight: 12, flexShrink: 0 }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="white" 
                      fontWeight={600} 
                      gutterBottom
                      sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
                    >
                      Working Hours
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="rgba(255,255,255,0.7)"
                      sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
                    >
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* CTA Button */}
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                endIcon={<ArrowRight />}
                sx={{ 
                  borderRadius: 0, 
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  backgroundImage: 'linear-gradient(to right, #FF5722, #FF8A65)',
                  boxShadow: '0 4px 15px rgba(255, 87, 34, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 87, 34, 0.4)'
                  }
                }}
              >
                Get in Touch
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Copyright Section */}
      <Box 
        sx={{ 
          bgcolor: '#0A0A0A', 
          py: 3,
          borderTop: '1px solid rgba(255,255,255,0.05)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography 
                variant="body2" 
                color="rgba(255,255,255,0.5)"
                sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
              >
                &copy; {currentYear} VectoSpaceDesign. All Rights Reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', md: 'flex-end' }, 
                gap: 3,
                flexWrap: 'wrap'
              }}>
                <Typography
                  component={RouterLink}
                  to="/privacy-policy"
                  variant="body2"
                  color="rgba(255,255,255,0.5)"
                  sx={{ 
                    textDecoration: 'none',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    transition: 'color 0.3s ease',
                    '&:hover': { color: '#FF5722' }
                  }}
                >
                  Privacy Policy
                </Typography>
                <Typography
                  component={RouterLink}
                  to="/terms-conditions"
                  variant="body2"
                  color="rgba(255,255,255,0.5)"
                  sx={{ 
                    textDecoration: 'none',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    transition: 'color 0.3s ease',
                    '&:hover': { color: '#FF5722' }
                  }}
                >
                  Terms & Conditions
                </Typography>
                <Typography
                  component={RouterLink}
                  to="/sitemap"
                  variant="body2"
                  color="rgba(255,255,255,0.5)"
                  sx={{ 
                    textDecoration: 'none',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    transition: 'color 0.3s ease',
                    '&:hover': { color: '#FF5722' }
                  }}
                >
                  Sitemap
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;